import { forwardRef, useState } from 'react';
import { cn } from '../../lib/utils';

interface SwitchProps {
    checked?: boolean;
    defaultChecked?: boolean;
    onChange?: (checked: boolean) => void;
    label?: string;
    description?: string;
    disabled?: boolean;
    className?: string;
    id?: string;
}

/**
 * Switch — Animated binary toggle. Use instead of <input type="checkbox"> in settings forms.
 *
 * @example
 * <Switch label="Allow Late Submission" checked={watch('allowLateSubmission')}
 *   onChange={(v) => setValue('allowLateSubmission', v)} />
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
    ({ checked: controlledChecked, defaultChecked = false, onChange, label, description, disabled = false, className, id }, ref) => {
        const [internalChecked, setInternalChecked] = useState(defaultChecked);
        const isChecked = controlledChecked !== undefined ? controlledChecked : internalChecked;

        const toggle = () => {
            if (disabled) return;
            const next = !isChecked;
            setInternalChecked(next);
            onChange?.(next);
        };

        const switchId = id || `switch-${label?.toLowerCase().replace(/\s+/g, '-')}`;

        return (
            <div className={cn('flex items-center justify-between gap-4', className)}>
                {(label || description) && (
                    <div className="flex flex-col gap-0.5">
                        {label && (
                            <label htmlFor={switchId} className="text-sm font-bold text-slate-800 cursor-pointer select-none">
                                {label}
                            </label>
                        )}
                        {description && (
                            <p className="text-xs text-slate-500">{description}</p>
                        )}
                    </div>
                )}
                <button
                    ref={ref}
                    id={switchId}
                    role="switch"
                    type="button"
                    aria-checked={isChecked}
                    onClick={toggle}
                    disabled={disabled}
                    className={cn(
                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
                        'transition-colors duration-200 ease-in-out',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2',
                        'disabled:opacity-40 disabled:cursor-not-allowed',
                        isChecked ? 'bg-teal-600' : 'bg-slate-200'
                    )}
                >
                    <span
                        className={cn(
                            'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg',
                            'transition-transform duration-200 ease-in-out',
                            isChecked ? 'translate-x-5' : 'translate-x-0'
                        )}
                    />
                </button>
            </div>
        );
    }
);
Switch.displayName = 'Switch';
